import React, { useRef, useState } from "react";
import { Box, Paper, styled, Typography, Chip } from "@mui/material";
import Chart from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";

const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: "none",
  transition: "box-shadow 0.3s ease-in-out",
  border: "1px solid #D9D9D9",
  height: "100%",
  "&:hover": {
    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
  },
}));

const CardHeaderWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(3),
  },
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const CardBodyWrapper = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  height: "70%",
  width: "100%",
  position: "relative",
}));

function CategoryDiagram({ data, onCategoryClick, selectedCategoryId }) {
  const { t, i18n } = useTranslation();
  const chartRef = useRef(null);
  // Remove local state since it's now controlled by parent
  // const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const processChartData = () => {
    if (!data || !data.categories || data.categories.length === 0) {
      return {
        labels: [t("charts.categories.noData", "No Data")],
        datasets: [
          {
            label: t("charts.categories.candidates", "Candidates"),
            data: [0],
            backgroundColor: "#E2E8F0",
            borderWidth: 0,
            borderRadius: 40,
          },
        ],
      };
    }

    const isFrenchlanguage = i18n.language === "fr";

    const labels = data.categories.map((category) =>
      isFrenchlanguage ? category.name_fr : category.name
    );

    const counts = data.categories.map((category) => category.count);

    // Define the same color scheme as ApplicationCadidatesChart
    const colors = [
      "#E2E8F0", 
      "#94A3B8", 
      "#475569", 
      "#1E293B", 
      "#003CBA",
    ];

    // Create background colors with highlighting for selected category
    const backgroundColors = counts.map((count, index) => {
      const category = data.categories[index];
      const isSelected = selectedCategoryId && category.id === selectedCategoryId;
      const baseColor = colors[index % colors.length];
      
      // If selected, make it more vibrant/darker
      if (isSelected) {
        return '#003CBA'; // Always use the blue color for selected
      }
      
      return baseColor;
    });

    // Create border widths for selected category
    const borderWidths = counts.map((count, index) => {
      const category = data.categories[index];
      return selectedCategoryId && category.id === selectedCategoryId ? 3 : 0;
    });

    return {
      labels,
      datasets: [
        {
          label: t("charts.categories.candidates", "Candidats"),
          data: counts,
          backgroundColor: backgroundColors,
          borderWidth: borderWidths,
          borderColor: '#1E293B',
          borderRadius: 6,
        },
      ],
    };
  };

  const chartData = processChartData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const clickedIndex = elements[0].index;
        const clickedCategory = data.categories[clickedIndex];
        
        // Toggle selection: if same category is clicked, deselect it
        if (selectedCategoryId === clickedCategory.id) {
          // Clear selection
          if (onCategoryClick) {
            onCategoryClick({ id: null });
          }
        } else {
          // Select new category
          if (onCategoryClick) {
            onCategoryClick(clickedCategory);
          }
        }
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#000",
        titleColor: "#fff",
        bodyColor: "#fff",
        bodyFont: { family: "'Inter', sans-serif", size: 12 },
        padding: 12,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const count = context.raw;
            const categoryName = context.label;
            return `${categoryName}: ${count} ${
              count === 1
                ? t("charts.categories.candidate", "candidate")
                : t("charts.categories.candidates", "candidates")
            }`;
          },
          afterLabel: (context) => {
            return "Click to filter candidates";
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          padding: 8,
          color: "#64748B",
          font: { family: "'Inter', sans-serif", size: 12 },
          maxRotation: 45,
          callback: function (value) {
            const label = this.getLabelForValue(value);
            return label.length > 12 ? label.substring(0, 12) + "..." : label;
          },
        },
      },
      y: {
        border: { display: false },
        grid: { color: "#E2E8F0", lineWidth: 0.5 },
        ticks: {
          padding: 8,
          color: "#64748B",
          font: { family: "'Inter', sans-serif", size: 12 },
          stepSize: 1,
          callback: (value) => (Math.floor(value) === value ? value : ""),
        },
        beginAtZero: true,
      },
    },
    elements: {
      bar: { borderRadius: 6, borderSkipped: false },
    },
    barPercentage: 0.6,
    categoryPercentage: 0.8,
    interaction: { mode: "index", intersect: false },
    animation: {
      duration: 1000,
      easing: "easeOutQuart",
    },
  };

  return (
    <StyledCard elevation={0}>
      <CardHeaderWrapper>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: { xs: "14px", sm: "16px" },
                color: "#00000080",
              }}
            >
              {t("charts.categories.title", "Categories Distribution")}
            </Typography>
            
            {/* Show selected category filter */}
            {selectedCategoryId && data && data.categories && (
              <Chip
                size="small"
                label={(() => {
                  const selectedCategory = data.categories.find(cat => cat.id === selectedCategoryId);
                  const isFrenchlanguage = i18n.language === "fr";
                  const categoryName = selectedCategory 
                    ? (isFrenchlanguage ? selectedCategory.name_fr : selectedCategory.name)
                    : "Unknown";
                  return `Filter: ${categoryName}`;
                })()}
                onDelete={() => {
                  // Clear selection by calling parent callback
                  if (onCategoryClick) {
                    onCategoryClick({ id: null });
                  }
                }}
                deleteIcon={<CloseIcon />}
                sx={{
                  backgroundColor: "#003CBA",
                  color: "white",
                  "& .MuiChip-deleteIcon": {
                    color: "white",
                  },
                }}
              />
            )}
          </Box>

          {data && data.total_candidate && (
            <Box
              sx={{
                backgroundColor: "#f8fafc",
                borderRadius: "8px",
                padding: "4px 12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "#64748b",
                }}
              >
                {t("charts.categories.total", "Total")}: {data.total_candidate}{" "}
                {data.total_candidate === 1
                  ? t("charts.categories.candidate", "candidate")
                  : t("charts.categories.candidates", "candidates")}
              </Typography>
            </Box>
          )}
        </Box>
      </CardHeaderWrapper>

      <CardBodyWrapper>
        <Box
          sx={{
            height: { xs: 250, sm: 300, md: 350 },
            width: "100%",
            position: "relative",
            "& canvas": {
              maxWidth: "100% !important",
              height: "90% !important",
            },
          }}
        >
          <Chart ref={chartRef} type="bar" data={chartData} options={chartOptions} />
        </Box>
      </CardBodyWrapper>
    </StyledCard>
  );
}

export default CategoryDiagram;
